import auth from '@/auth/auth';
import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import { BadgeDropDownValue } from '@/components/BadgeDropDown/BadgeDropDown.vue';
import { eventVariantStates, EventVariantStatus } from '@/event/types';
import {
    ExternalEventVariantStatus,
    UpdateEventVariantDocument,
    UpdateExternalEventVariantDocument,
    UpdateProjectVariantDocument,
} from '@/generated/graphql';
import { projectVariantStates, ProjectVariantStatus } from '@/project/types';
import { useMyUser } from '@/user/composables/useMyUser';
import { useMutation } from '@vue/apollo-composable';
import { useFluent } from 'fluent-vue';
import { computed, Ref, toValue } from 'vue';
import { VariantCardVariant, VariantStatus } from '../types';
import { getVariantStatusLabel, variantStatusToBadgeVariant } from '../utils';

export function useVariantStatusDropdown(
    variantTypeRef: Ref<AllocationGroupType>,
    variantRef: Ref<VariantCardVariant | undefined>,
    variantsRef: Ref<VariantCardVariant[]>,
    setVariantsRef: (variants: VariantCardVariant[]) => void,
    createStatusChangeEntry: (oldStatus: string, newStatus: string) => void,
) {
    const fluent = useFluent();
    const { currentUser } = useMyUser();
    const isAdmin = auth.isAdmin();

    const document = {
        Event: UpdateEventVariantDocument,
        Project: UpdateProjectVariantDocument,
        ExternalEvent: UpdateExternalEventVariantDocument,
    };

    const updateMutation = useMutation(document[toValue(variantTypeRef)], {
        fetchPolicy: 'no-cache',
    });

    const dropDownDisabledExplanation = computed(() => {
        return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
    });

    const dropDownStatus = computed(() => {
        if (dropDownDisabledExplanation.value) return 'Disabled';
        if (updateMutation.loading.value) return 'Loading';
        if (updateMutation.error.value) return 'Retry';
        return 'Default';
    });

    function getVariantStatusItem(
        status: EventVariantStatus | ProjectVariantStatus | ExternalEventVariantStatus,
    ): BadgeDropDownValue {
        return {
            id: status,
            label: getVariantStatusLabel(toValue(variantTypeRef), status),
            variant: variantStatusToBadgeVariant(toValue(variantTypeRef), status),
        };
    }

    function getVariantStatusOptionItems(
        status: EventVariantStatus | ProjectVariantStatus | ExternalEventVariantStatus,
    ): (BadgeDropDownValue & { isSelected: boolean })[] {
        const externalEventVariantStatusOrder = ['fix', 'option', 'discontinued'];
        switch (toValue(variantTypeRef)) {
            case 'Event':
                return eventVariantStates.map((s) => ({
                    id: s,
                    label: getVariantStatusLabel(toValue(variantTypeRef), s),
                    variant: variantStatusToBadgeVariant(toValue(variantTypeRef), s),
                    isSelected: status === s,
                }));
            case 'Project':
                return projectVariantStates.map((s) => ({
                    id: s,
                    label: getVariantStatusLabel(toValue(variantTypeRef), s),
                    variant: variantStatusToBadgeVariant(toValue(variantTypeRef), s),
                    isSelected: status === s,
                }));
            case 'ExternalEvent':
                return Object.values(ExternalEventVariantStatus)
                    .sort(
                        (x, y) =>
                            externalEventVariantStatusOrder.indexOf(x) - externalEventVariantStatusOrder.indexOf(y),
                    )
                    .map((s) => ({
                        id: s,
                        label: getVariantStatusLabel(toValue(variantTypeRef), s),
                        variant: variantStatusToBadgeVariant(toValue(variantTypeRef), s),
                        isSelected: status === s,
                    }));
        }
    }

    async function handleBadgeDropdown(status: string | ExternalEventVariantStatus, variantId: string) {
        const variant = toValue(variantRef);
        if (variant && dropDownStatus.value !== 'Disabled') {
            const oldStatus = variant.status;
            if (oldStatus === status) return;

            const user = toValue(currentUser);
            const feedId = variant.changesId;

            if (user && feedId) {
                const result = await updateMutation.mutate({
                    id: variantId,
                    feedId,
                    status: status as any, // FIXME better handling of typescript error?
                });
                const variantResult = result?.data?.update?.variant;
                if (variantResult) {
                    createStatusChangeEntry(oldStatus, status);
                    setVariantsRef(
                        toValue(variantsRef).map((v) =>
                            v.id === variantId
                                ? {
                                      ...v,
                                      status: variantResult.status as VariantStatus,
                                  }
                                : v,
                        ),
                    );
                }
            }
        }
    }

    return {
        dropDownStatus,
        dropDownDisabledExplanation,
        getVariantStatusItem,
        getVariantStatusLabel,
        getVariantStatusOptionItems,
        handleBadgeDropdown,
    };
}
